import { Badge } from '../../primitives/badge';
import { cn } from '../../utils/cn';

import type { JSX } from 'react';

interface BadgeBooleanProps extends React.ComponentPropsWithoutRef<typeof Badge> {
  isAnimated?: boolean;
  off?: {
    icon?: JSX.Element;
    text?: string | JSX.Element;
  };
  on?: {
    icon?: JSX.Element;
    text?: string | JSX.Element;
  };
  value: boolean;
}

/**
 * `BadgeBoolean` is a visual component based on `Badge` that represents a boolean state (active/inactive).
 *
 * @param className **optional** Custom CSS classes to style the component.
 * @param isAnimated **optional** Adds a "ping" animation to the indicator when the value is `true`. Defaults to `false`.
 * @param off **optional** Configuration for the `false` state. Supports a custom `icon` and `text`.
 * @param on **optional** Configuration for the `true` state. Supports a custom `icon` and `text`.
 * @param size **optional** Inherits from `Badge` props. Defaults to `md`.
 * @param value Boolean that controls the badge state (`true` = active, `false` = inactive).
 * @param variant **optional** Inherits from `Badge` props. Defaults to `"outline"`.
 *
 * **Behavior:**
 * - If both `on` and `off` icons are provided, they are shown instead of the ping indicator.
 * - When `isAnimated` is true, a "ping" animation is applied to the icon or indicator.
 * - Defaults to `Active` and `Inactive` if no custom text is provided.
 *
 * @example
 * ```tsx
 * <BadgeBoolean value={true} isAnimated />
 * <BadgeBoolean
 *    value={false}
 *    on={{ icon: <CheckIcon />, text: "Yes" }}
 *    off={{ icon: <XIcon />, text: "No" }}
 * />
 * <BadgeBoolean
 *    on={{
 *      icon: <LockClosedIcon className="text-primary" />,
 *      text: <span className="text-primary">Privado</span>,
 *    }}
 *    off={{ icon: <LockOpen2Icon />, text: 'Público' }}
 *    value={!Boolean(row.getValue('allowAnonymousAccess'))}
 * />
 * ```
 */
export function BadgeBoolean({
  className,
  isAnimated = false,
  off,
  on,
  size = 'md',
  value,
  variant = 'outline',
}: BadgeBooleanProps) {
  const ping = (
    <div
      className={cn('size-3 rounded-full', {
        'bg-muted-foreground/50': !value,
        'bg-success': value,
      })}
    >
      {isAnimated && (
        <div
          className={cn('h-full w-full rounded-full', {
            'animate-[ping_1500ms_ease-in-out_infinite] bg-success': value,
          })}
        />
      )}
    </div>
  );

  const icon = (
    <div className="relative">
      {isAnimated && (
        <div
          className={cn('absolute right-0 h-[25%] w-[25%] rounded-full', {
            'animate-[ping_1500ms_ease-in-out_infinite] bg-primary': value,
          })}
        />
      )}
      {value ? on?.icon : off?.icon}
    </div>
  );

  return (
    <Badge
      size={size}
      className={cn('flex-auto items-center gap-2 select-none', className)}
      variant={variant}
    >
      {on?.icon && off?.icon ? icon : ping}
      {value ? (on?.text ?? 'Activo') : (off?.text ?? 'Inactivo')}
    </Badge>
  );
}
